import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, MailWarning, RefreshCcw, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EVENT_LABELS = {
  order_confirmation: "Order confirmation",
  order_dispatched: "Order dispatched",
  order_delivered: "Order delivered",
  return_status: "Return / exchange status",
  price_lock_reminder: "Price-lock reminder",
  notify_me: "Back-in-stock alert",
  admin_alert: "Admin alerts",
};

export default function AdminEmail() {
  const [status, setStatus] = useState(null);
  const [health, setHealth] = useState(null);
  const [suppressions, setSuppressions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [records, setRecords] = useState([]);

  const load = useCallback(async () => {
    try {
      const [st, he, sp, se] = await Promise.all([
        api.get("/admin/email/status"),
        api.get("/admin/email/health"),
        api.get("/admin/email/suppressions"),
        api.get("/admin/settings"),
      ]);
      setStatus(st.data);
      setHealth(he.data);
      setSuppressions(sp.data);
      setSettings(se.data);
      setRecords(st.data.records || []);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveAuth = async () => {
    try {
      await api.put("/admin/settings", {
        auth_otp_channel: settings.auth_otp_channel,
        otp_expiry_minutes: Number(settings.otp_expiry_minutes) || 10,
        otp_resend_cooldown_seconds: Number(settings.otp_resend_cooldown_seconds) || 30,
        otp_max_attempts: Number(settings.otp_max_attempts) || 5,
        email_monthly_limit: Number(settings.email_monthly_limit) || 3000,
        notification_channels: settings.notification_channels || {},
      });
      toast.success("Auth & notification settings saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveDns = async () => {
    try {
      await api.put("/admin/email/dns", { transactional_subdomain: status.subdomain, records });
      toast.success("DNS records saved — verification status reset until re-checked");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const verifyDns = async () => {
    setVerifying(true);
    try {
      await saveDns();
      const r = await api.post("/admin/email/dns/verify");
      setRecords(r.data.records);
      setStatus((s) => ({ ...s, domain_verified: r.data.verified, send_gate: r.data.verified && s.api_key_set ? "live" : "deferred" }));
      if (r.data.verified) toast.success("Domain verified — transactional email is live");
      else toast.warning("Some records are not right yet — see each row below");
    } catch (e) { toast.error(errMsg(e)); } finally { setVerifying(false); }
  };

  const removeSuppression = async (email) => {
    try {
      await api.delete(`/admin/email/suppressions/${encodeURIComponent(email)}`);
      setSuppressions((s) => s.filter((x) => x.email !== email));
      toast.success(`${email} removed from suppression list`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!status || !health || !settings) return <div className="p-8 text-sm text-neutral-500">Loading…</div>;

  const funnel = health.otp_funnel || {};

  return (
    <div className="space-y-8" data-testid="admin-email-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold">Auth, Email & Notifications</h1>
        <p className="text-sm text-neutral-500 mt-1">Passwordless sign-in channel, transactional email deliverability, and notification routing.</p>
      </div>

      {/* Send gate warning */}
      {status.send_gate !== "live" && (
        <div className="border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2" data-testid="send-gate-warning">
          <MailWarning size={17} className="mt-0.5 shrink-0" />
          <div>
            <strong>Customer email is paused.</strong> {status.deferred_note}
            {!status.api_key_set && <span className="block mt-1">Add <code className="font-mono text-xs">RESEND_API_KEY</code> to the backend environment first.</span>}
            {status.api_key_set && !status.domain_verified && <span className="block mt-1">Verify the sending domain below to go live.</span>}
          </div>
        </div>
      )}

      {/* Sign-in channel */}
      <section className="border border-neutral-200 bg-white p-6" data-testid="auth-channel-section">
        <h2 className="font-heading font-semibold text-lg">Sign-in channel</h2>
        <p className="text-xs text-neutral-500 mt-1">Indian mobile shoppers are used to phone-first OTP; email-first adds slight friction and carries deliverability risk. It is the right call while SMS/WhatsApp are unavailable — free and immediate. Revisit once a provider connects; decide on the OTP completion data below, not opinion.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
          <div>
            <Label>OTP channel</Label>
            <select value={settings.auth_otp_channel || "email"} onChange={(e) => setSettings({ ...settings, auth_otp_channel: e.target.value })}
              className="w-full h-10 border border-neutral-200 rounded-sm px-3 text-sm bg-white" data-testid="otp-channel-select">
              <option value="email">Email (live)</option>
              <option value="sms">SMS (stub — connect MSG91/Twilio)</option>
              <option value="whatsapp">WhatsApp (stub — connect WABA)</option>
            </select>
          </div>
          <div><Label>Code expiry (min)</Label><Input type="number" value={settings.otp_expiry_minutes ?? 10} onChange={(e) => setSettings({ ...settings, otp_expiry_minutes: e.target.value })} data-testid="otp-expiry-input" /></div>
          <div><Label>Resend cooldown (s)</Label><Input type="number" value={settings.otp_resend_cooldown_seconds ?? 30} onChange={(e) => setSettings({ ...settings, otp_resend_cooldown_seconds: e.target.value })} data-testid="otp-cooldown-input" /></div>
          <div><Label>Max attempts</Label><Input type="number" value={settings.otp_max_attempts ?? 5} onChange={(e) => setSettings({ ...settings, otp_max_attempts: e.target.value })} data-testid="otp-attempts-input" /></div>
        </div>
      </section>

      {/* Notification routing */}
      <section className="border border-neutral-200 bg-white p-6" data-testid="notification-routing-section">
        <h2 className="font-heading font-semibold text-lg">Notification routing</h2>
        <p className="text-xs text-neutral-500 mt-1">Per type, not one global switch — from October 2026 WhatsApp utility/service messages inside the customer-service window become chargeable, so route deliberately. Transactional messages always send; marketing consent never gates them.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
          {Object.entries(EVENT_LABELS).map(([key, label]) => (
            <div key={key} className="flex items-center justify-between border border-neutral-200 px-3 py-2">
              <span className="text-sm">{label}</span>
              <select value={(settings.notification_channels || {})[key] || "email"}
                onChange={(e) => setSettings({ ...settings, notification_channels: { ...(settings.notification_channels || {}), [key]: e.target.value } })}
                className="h-8 border border-neutral-200 rounded-sm px-2 text-xs bg-white" data-testid={`route-${key}`}>
                <option value="email">Email</option>
                <option value="sms">SMS (stub)</option>
                <option value="whatsapp">WhatsApp (stub)</option>
              </select>
            </div>
          ))}
        </div>
        <Button onClick={saveAuth} className="mt-4 bg-neutral-900 hover:bg-brand text-white btn-press" data-testid="save-auth-settings-btn">Save channel & routing</Button>
      </section>

      {/* Sending domain */}
      <section className="border border-neutral-200 bg-white p-6" data-testid="email-domain-section">
        <h2 className="font-heading font-semibold text-lg">Sending domain (Resend)</h2>
        <p className="text-xs text-neutral-500 mt-1">
          Sending from a dedicated subdomain keeps transactional deliverability isolated from any future marketing volume.
          Currently sending as <span className="font-mono">{status.from_name} &lt;{status.from_address}&gt;</span>
          {status.reply_to ? <> · replies go to <span className="font-mono">{status.reply_to}</span></> : ""}.
          API key: <strong>{status.api_key_set ? "set" : "missing"}</strong> · Domain:{" "}
          <strong className={status.domain_verified ? "text-emerald-700" : "text-amber-700"}>{status.domain_verified ? "verified" : "not verified"}</strong>
        </p>
        <div className="mt-3 max-w-sm">
          <Label>Sending subdomain</Label>
          <Input value={status.subdomain} onChange={(e) => setStatus({ ...status, subdomain: e.target.value })} className="font-mono" data-testid="subdomain-input" />
        </div>
        <p className="text-xs text-neutral-500 mt-4">
          In Cloudflare → DNS → Records, add each row below exactly. DKIM and MX values come from the Resend dashboard (Domains → add {status.subdomain}) — paste them here, then press Verify.
        </p>
        <div className="mt-3 space-y-2">
          {records.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center border border-neutral-200 px-3 py-2" data-testid={`dns-record-${i}`}>
              <span className="col-span-1 font-mono text-xs font-semibold">{r.type}</span>
              <span className="col-span-3 font-mono text-xs truncate" title={r.host}>{r.host}</span>
              <Input value={r.value} onChange={(e) => setRecords(records.map((x, j) => j === i ? { ...x, value: e.target.value } : x))}
                placeholder="paste value" className="col-span-5 h-8 font-mono text-xs" data-testid={`dns-value-${i}`} />
              <span className="col-span-2 text-[11px] text-neutral-500 truncate" title={r.purpose}>{r.purpose}</span>
              <span className="col-span-1 text-right">
                {r.state === "ok" ? <CheckCircle2 size={16} className="text-emerald-600 inline" data-testid={`dns-ok-${i}`} /> :
                 r.state ? <XCircle size={16} className="text-red-500 inline" data-testid={`dns-bad-${i}`} /> : null}
              </span>
              {r.state && r.state !== "ok" && <p className="col-span-12 text-[11px] text-red-600 font-mono">{r.state}{r.found?.length ? ` — resolves to: ${r.found.join(", ")}` : ""}</p>}
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-4">
          <Button onClick={verifyDns} disabled={verifying} className="bg-neutral-900 hover:bg-brand text-white btn-press" data-testid="dns-verify-btn">
            {verifying ? "Checking DNS…" : "Verify DNS records"}
          </Button>
          <Button variant="outline" onClick={saveDns} data-testid="dns-save-btn">Save records</Button>
        </div>
        <p className="text-xs text-neutral-500 mt-4 border-t border-neutral-100 pt-3" data-testid="inbound-note">
          <strong>Inbound mail:</strong> {status.inbound_note}
        </p>
      </section>

      {/* Delivery health */}
      <section className="border border-neutral-200 bg-white p-6" data-testid="email-health-section">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-semibold text-lg">Delivery health</h2>
          <Button variant="outline" size="sm" onClick={load} data-testid="health-refresh-btn"><RefreshCcw size={14} className="mr-1" /> Refresh</Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
          <div className="border border-neutral-200 p-4">
            <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-400">This month</p>
            <p className="font-heading text-2xl font-semibold mt-1" data-testid="email-month-sends">{health.month_sends} <span className="text-sm text-neutral-400">/ {health.monthly_limit}</span></p>
            <div className="h-1.5 bg-neutral-100 mt-2"><div className={`h-full ${health.alert === "90" ? "bg-red-500" : health.alert === "70" ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, health.pct_used)}%` }} /></div>
            {health.alert && <p className="text-[11px] font-mono text-red-600 mt-1" data-testid="email-volume-alert">{health.alert}% of plan used</p>}
          </div>
          <div className="border border-neutral-200 p-4">
            <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-400">Delivery rate</p>
            <p className="font-heading text-2xl font-semibold mt-1" data-testid="email-delivery-rate">{health.delivery_rate == null ? "—" : `${health.delivery_rate}%`}</p>
            <p className="text-[11px] text-neutral-500 mt-1">bounces {health.bounces} · complaints {health.complaints} · deferred {health.deferred}</p>
          </div>
          <div className="border border-neutral-200 p-4">
            <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-400">OTP funnel (30d)</p>
            <p className="font-heading text-2xl font-semibold mt-1" data-testid="otp-completion">{funnel.completion_rate == null ? "—" : `${funnel.completion_rate}%`}</p>
            <p className="text-[11px] text-neutral-500 mt-1">requested {funnel.requested} · verified {funnel.verified} · failed {funnel.failed}</p>
          </div>
          <div className="border border-neutral-200 p-4">
            <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-400">Suppressed</p>
            <p className="font-heading text-2xl font-semibold mt-1" data-testid="suppression-count">{health.suppressions}</p>
            <p className="text-[11px] text-neutral-500 mt-1">hard bounces + complaints auto-blocked</p>
          </div>
        </div>
        <h3 className="text-sm font-semibold mt-6 mb-2">Recent sends</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="email-events-table">
            <thead><tr className="text-left text-neutral-400 font-mono uppercase tracking-wider">
              <th className="py-1.5 pr-3">When</th><th className="pr-3">To</th><th className="pr-3">Subject</th><th className="pr-3">Status</th><th>Detail</th>
            </tr></thead>
            <tbody>
              {(health.recent || []).map((ev, i) => (
                <tr key={i} className="border-t border-neutral-100">
                  <td className="py-1.5 pr-3 font-mono whitespace-nowrap">{ev.at ? new Date(ev.at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</td>
                  <td className="pr-3 font-mono">{ev.to}</td>
                  <td className="pr-3 max-w-56 truncate">{ev.subject}</td>
                  <td className="pr-3"><span className={`font-mono ${ev.status === "sent" || ev.status === "delivered" ? "text-emerald-700" : ev.status === "deferred" ? "text-amber-700" : "text-red-600"}`}>{ev.status}</span></td>
                  <td className="text-neutral-500">{ev.detail}</td>
                </tr>
              ))}
              {!(health.recent || []).length && <tr><td colSpan={5} className="py-4 text-neutral-400">No sends logged yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {/* Suppression list */}
      <section className="border border-neutral-200 bg-white p-6" data-testid="suppression-section">
        <h2 className="font-heading font-semibold text-lg">Suppression list</h2>
        <p className="text-xs text-neutral-500 mt-1">Hard bounces and complaints land here automatically so repeat sends to dead addresses never damage domain reputation. Remove only if you know the address is alive.</p>
        <div className="mt-3 space-y-2">
          {suppressions.map((s) => (
            <div key={s.email} className="flex items-center justify-between border border-neutral-200 px-3 py-2 text-sm" data-testid={`suppression-${s.email}`}>
              <span className="font-mono">{s.email} <span className="text-neutral-400 text-xs">({s.reason})</span></span>
              <Button size="sm" variant="outline" onClick={() => removeSuppression(s.email)} data-testid={`unsuppress-${s.email}`}>Remove</Button>
            </div>
          ))}
          {!suppressions.length && <p className="text-sm text-neutral-400">No suppressed addresses.</p>}
        </div>
      </section>
    </div>
  );
}
